import { missingImageUrl } from '~/utils/format/imageUrl'
import { DELIVERED_WIDTHS, MAX_DELIVERY_WIDTH, PLACEHOLDER_DEFAULT_WIDTH } from '~/utils/format/imageSizes'

/**
 * Widths the placeholder is delivered at — every width a real image can be
 * delivered at, plus the ceiling, so a placeholder request reuses a transform
 * the CDN has likely cached. Both sides read `imageSizes` rather than repeating
 * the literals, because that cache-reuse property is exactly what silently
 * breaks when the two lists drift.
 */
const PLACEHOLDER_WIDTHS: readonly number[] = [...DELIVERED_WIDTHS, MAX_DELIVERY_WIDTH]

/**
 * Cloudinary's `d_` substituted the default image at the width the caller
 * asked for. A fixed 360px placeholder upscales badly in a hi-res slot — the
 * PDP hero renders at 1200 with `object-fit: contain`, so a 2KB placeholder
 * stretched across it is visibly blurry.
 *
 * The element's own layout box is the size that actually matters, and measuring
 * it keeps the sizing correct without threading a transform through every call
 * site — which a call site added later could forget.
 */
export const placeholderWidthFor = (image: HTMLImageElement): number => {
  const dpr = typeof window === 'undefined' ? 1 : Math.min(window.devicePixelRatio || 1, 2)
  const needed = Math.ceil((image.clientWidth || 0) * dpr)

  // A box with no measurable width tells us nothing about the slot, so it gets
  // the documented default rather than the smallest width in the list.
  if (needed === 0) {
    return PLACEHOLDER_DEFAULT_WIDTH
  }

  return PLACEHOLDER_WIDTHS.find(width => width >= needed) ?? MAX_DELIVERY_WIDTH
}

/**
 * Records the src the fallback was applied *for*, rather than a bare "already
 * applied" flag.
 *
 * The distinction matters because Vue reuses DOM nodes: `WishlistSummaryCard`
 * renders `v-for="index in 4"` keyed by index, so the same four `<img>`
 * elements persist across changes to the list. A boolean flag would suppress
 * the fallback for every later image bound to a slot that had already failed
 * once, leaving the browser's broken-image icon. Comparing against the failing
 * src instead means only the placeholder failing stops the swap — which is the
 * actual loop condition.
 */
const APPLIED_FOR_KEY = 'imageFallbackAppliedFor'

/**
 * Client-side replacement for Cloudinary's `d_` default-image parameter, for
 * product images rendered as a plain `<img>` rather than through `SImage`.
 *
 * `SImage` owns this itself, reactively — it binds `src`, so a DOM write there
 * would be overwritten on the next render. These call sites bind a plain
 * computed and have no such loop, so writing the element is safe and avoids
 * threading failure state through each one.
 *
 * Cloudinary substituted a missing image at delivery time and returned 200.
 * Cloudflare has no equivalent, and `/cdn-cgi/` fires neither Rules Snippets nor
 * Workers routes (measured 2026-09-08), so nothing server-side can intercept the
 * 404 (ADO 62991).
 */
export const useImageFallback = () => {
  const onImageError = (event: Event): void => {
    const image = event.target as HTMLImageElement | null
    if (!image || image.tagName !== 'IMG') {
      return
    }

    // The placeholder itself failing is the loop condition — stop there. Any
    // other src is a new image on a possibly-reused element, so it gets the
    // fallback even if this element carried one before. Checked before the
    // placeholder is built, so the loop path costs neither a layout read nor a
    // URL serialization.
    if (image.getAttribute('src') === image.dataset[APPLIED_FOR_KEY]) {
      return
    }

    const placeholder = missingImageUrl({ width: placeholderWidthFor(image), format: 'auto' })

    // Drop the candidate list first: the handler cannot see which candidate the
    // browser chose, and leaving `srcset` in place lets it reselect one of the
    // original URLs and overwrite the placeholder set below.
    image.removeAttribute('srcset')
    image.setAttribute('src', placeholder)
    image.dataset[APPLIED_FOR_KEY] = placeholder
  }

  return { onImageError }
}
